/*
 * Copyright (C) Compliance Audit Systems Limited - All Rights Reserved
 * Unauthorised copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Sudara Fernando <sudara@complianceauditsystems.com>, ${DATE}
 *
 */

import {Component, EventEmitter, Input, Optional, Output} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {AcabimAuthRole, AcabimSolution, getAllEnumValues, getErrorMessage, parseAuthRole, parseSolution} from '@cas-hub/acabim-common-client';
import {NGXLogger} from 'ngx-logger';
import {MessageService} from 'primeng/api';
import {OrganisationSeat} from '../../../../_models/organisation/organisation-seats.model';
import {OrganisationSeatsService} from '../../../../_services/organisation/organisation-seats.service';

@Component({
    selector: 'acabim-admin-add-organisation-seat',
    templateUrl: './add-organisation-seat.component.html',
    styleUrls: ['./add-organisation-seat.component.scss']
})
export class AddOrganisationSeatComponent {

    @Output() public trialRequestCreated = new EventEmitter<OrganisationSeat>();

    /**
     * Only set if this form is being used by a Super Admin. Super admins can request trial seats on behalf of an organisation. If not set, then the current user's organisation
     * will be used. If this is set, and if the current user is not a super admin, the current user's organisation will be used instead, ignoring this value.
     */
    @Input() public superAdminOverrideOrganisationId?: string;

    public currentSolutions: { name: string, id: number }[] = [{name: 'Not restricted', id: -1}];
    public currentRoles: { name: string, id: number }[] = [
        {name: 'Not restricted', id: -1},
        {name: AcabimAuthRole[AcabimAuthRole.User], id: AcabimAuthRole.User},
        {name: AcabimAuthRole[AcabimAuthRole.Admin], id: AcabimAuthRole.Admin}
    ];

    public addSeatForm: FormGroup;

    private get selectedRestrictedSolution(): AcabimSolution | undefined {
        const restrictedSolutionValue: { name: string, id: number } | undefined = this.addSeatForm.get('restrictedSolution')?.value;
        if (restrictedSolutionValue && restrictedSolutionValue.id > -1) {
            return parseSolution(restrictedSolutionValue.name);
        }

        return undefined;
    }

    private get selectedRestrictedRole(): AcabimAuthRole | undefined {
        const restrictedRoleValue: { name: string, id: number } | undefined = this.addSeatForm.get('restrictedRole')?.value;
        if (restrictedRoleValue && restrictedRoleValue.id > -1) {
            return parseAuthRole(restrictedRoleValue.name);
        }

        return undefined;
    }

    public get heading(): string {
        return this.superAdminOverrideOrganisationId ? 'Add Trial Seats' : 'Request Trial Seats';
    }

    public get requestButtonText(): string {
        return this.superAdminOverrideOrganisationId ? 'Add Seat' : 'Request Seat';
    }

    constructor(private readonly seatsService: OrganisationSeatsService,
                private readonly messageService: MessageService,
                @Optional() private readonly logger?: NGXLogger) {
        for (const solution of getAllEnumValues(AcabimSolution)) {
            this.currentSolutions.push({name: solution, id: Number(AcabimSolution[solution as any])});
        }

        this.addSeatForm = new FormGroup({
            numberOfSeats: new FormControl(1, [Validators.required, Validators.min(1)]),
            restrictedSolution: new FormControl(this.currentSolutions[0]),
            restrictedRole: new FormControl(this.currentRoles[0])
        });
    }

    public requestTrialSeats(): void {
        if (this.addSeatForm.invalid) {
            this.logger?.error('[AddOrganisationSeatComponent] Form Validation Error');
            return;
        }

        const numOfSeats = this.addSeatForm.get('numberOfSeats')?.value;

        this.seatsService.addPurchaseOrder(numOfSeats ? Number(numOfSeats) : 1, this.selectedRestrictedSolution, this.selectedRestrictedRole,
                this.superAdminOverrideOrganisationId)
            .subscribe(v => this.trialRequestCreated.emit(v), error => {
                this.logger?.error('[AddOrganisationSeatComponent] Error requesting trial seat', error);
                this.messageService.add(getErrorMessage(error, 'Add trial seat error', 'add-organisation-seat-component'));
            }, () => {
                this.addSeatForm.reset();
            });
    }
}
